import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-store.js';
import { beginLogin, cognitoEnabled } from '../lib/cognito.js';
import { initials } from '../lib/format.js';

/**
 * Sign in. Production builds (VITE_AUTH_* baked in) redirect to the Cognito
 * Hosted UI; dev builds show the dev-IdP picker. Both styled on the brand
 * navy gradient.
 */
export function LoginScreen() {
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  const [redirecting, setRedirecting] = useState(false);

  const useCognito = cognitoEnabled();

  const users = useQuery({
    queryKey: ['dev-auth', 'candidates'],
    queryFn: ({ signal }) => api.auth.candidates(signal),
    staleTime: 60_000,
    enabled: !useCognito,
  });

  const login = useMutation({
    mutationFn: (email: string) => api.auth.login(email),
    onSuccess: (res) => {
      setSession({ token: res.accessToken, expiresIn: res.expiresIn, user: res.user });
      void navigate({ to: '/' });
    },
  });

  if (useCognito) {
    return (
      <div className="login">
        <div className="logo">
          <svg width="46" height="46" viewBox="0 0 64 64" fill="none" aria-hidden>
            <path
              d="M32 8c-9 0-16 5-16 14 0 5 1 8 3 14l5 14c1 3 3 5 5 5 3 0 4-3 5-7l1-7c0-1 1-2 2-2s2 1 2 2l1 7c1 4 2 7 5 7 2 0 4-2 5-5l5-14c2-6 3-9 3-14 0-9-7-14-16-14h-10z"
              fill="#1E5BA8"
            />
          </svg>
        </div>
        <h2>K-Dental</h2>
        <div className="tagline">Sales App</div>

        <div className="signin-card">
          <div className="lead">
            Live inventory, customer pricing &amp; ordering —<br />
            sign in with your work email to get started.
          </div>
          <button
            type="button"
            className="signin-btn"
            disabled={redirecting}
            onClick={() => {
              setRedirecting(true);
              void beginLogin();
            }}
          >
            {redirecting ? 'Opening secure sign-in…' : 'Sign in'}
          </button>
          <div className="signin-note">You'll be redirected to a secure sign-in page.</div>
        </div>

        <div className="footer">Bridged to Spire · Dentry platform · Gemini Logic</div>
      </div>
    );
  }

  return (
    <div className="login">
      <div className="logo">
        <svg width="46" height="46" viewBox="0 0 64 64" fill="none" aria-hidden>
          <path
            d="M32 8c-9 0-16 5-16 14 0 5 1 8 3 14l5 14c1 3 3 5 5 5 3 0 4-3 5-7l1-7c0-1 1-2 2-2s2 1 2 2l1 7c1 4 2 7 5 7 2 0 4-2 5-5l5-14c2-6 3-9 3-14 0-9-7-14-16-14h-10z"
            fill="#1E5BA8"
          />
        </svg>
      </div>
      <h2>K-Dental</h2>
      <div className="tagline">Sales App</div>

      <div className="pickers">
        {users.isPending && <div style={{ opacity: 0.7, fontSize: 14 }}>Loading users…</div>}
        {users.isError && <div style={{ opacity: 0.85, fontSize: 13 }}>{users.error.message}</div>}
        {users.data?.map((u) => (
          <button
            key={u.id}
            type="button"
            className="picker"
            disabled={login.isPending}
            onClick={() => login.mutate(u.email)}
          >
            <span
              className="avatar"
              style={{ background: 'rgba(255,255,255,.16)', color: '#fff' }}
            >
              {initials(u.displayName)}
            </span>
            <span className="who">
              <div style={{ fontWeight: 600 }}>{u.displayName}</div>
              <div className="role">{u.role}</div>
            </span>
          </button>
        ))}
        {login.isError && (
          <div style={{ fontSize: 13, opacity: 0.9 }}>Login failed: {login.error.message}</div>
        )}
      </div>

      <div className="footer">Bridged to Spire · v1.0 · Gemini Logic</div>
    </div>
  );
}
